import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateInventur, useImportPreview, type InventurImportPreview } from '@/api/inventur'
import { importScope } from '@/lib/inventur-analyse'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'

export function InventurImport() {
  const navigate = useNavigate(), preview = useImportPreview(), create = useCreateInventur()
  const [text, setText] = useState(''), [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<InventurImportPreview | null>(null), [error, setError] = useState('')
  const version = useRef(0), fileInput = useRef<HTMLInputElement>(null)
  function reset() { version.current++; setReport(null); setError('') }
  async function check() {
    const revision = version.current
    setError(''); setReport(null)
    try {
      const result = await preview.mutateAsync(file ? { file } : { text })
      if (version.current === revision) setReport(result)
    } catch (e) { if (version.current === revision) setError(`${e instanceof Error ? e.message : 'Liste konnte nicht geprüft werden'}. Eingabe prüfen und erneut versuchen.`) }
  }
  const scope = report ? importScope(report) : null
  const unique = report?.rows.filter(r => !r.duplicate) ?? []
  async function start() {
    if (!scope?.merchant_skus.length) return
    setError('')
    try { const result = await create.mutateAsync({ mode: 'produkt', scope }); navigate(`/warehouse/inventur/${result.id}`) }
    catch (e) { setError(`${e instanceof Error ? e.message : 'Anlegen fehlgeschlagen'}. Vorschau bleibt erhalten; bitte erneut versuchen.`) }
  }
  return <section className="space-y-4" aria-label="Inventur aus Artikelliste">
    <p className="text-sm text-ink-2">Excel- oder CSV-Datei auswählen oder eine Artikelspalte einfügen. Händlernummern und SKU werden vor dem Anlegen geprüft.</p>
    <div className="grid gap-4 md:grid-cols-2">
      <label className="space-y-1 text-sm">Excel- oder CSV-Datei
        <input ref={fileInput} aria-label="Excel- oder CSV-Datei" className="block w-full rounded border border-line p-2 focus-visible:outline-2 focus-visible:outline-primary" type="file" accept=".xlsx,.csv" onChange={e => {
          reset(); setFile(e.target.files?.[0] ?? null); setText('')
        }} />
        <span className="block text-xs text-ink-2">Maximal 5 MB. Die Datei wird nur zur Vorschau gelesen.</span>
      </label>
      <label className="space-y-1 text-sm">Artikelspalte einfügen
        <Textarea value={text} rows={5} placeholder={'Eine Nummer je Zeile\n*151\nSKU-123'} onChange={e => {
          reset(); setText(e.target.value); setFile(null); if (fileInput.current) fileInput.current.value = ''
        }} />
      </label>
    </div>
    <Button variant="outline" disabled={preview.isPending || create.isPending || (!file && !text.trim())} onClick={() => void check()}>{preview.isPending ? 'Prüfe Liste…' : 'Liste prüfen'}</Button>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {report && <>
      <p role="status" className="text-sm tabular-nums">{scope!.merchant_skus.length} Artikel bereit, {unique.filter(r => r.status === 'kein_bestand').length} ohne Bestand, {unique.filter(r => r.status === 'unbekannt').length} unbekannt, {unique.filter(r => r.status === 'mehrdeutig').length} mehrdeutig.</p>
      <div className="max-h-96 overflow-auto rounded-lg border border-line"><table className="w-full text-sm text-left">
        <thead className="sticky top-0 bg-panel"><tr>{['Eingabe', 'Artikel-Nr.', 'Prüfung', 'Paletten', 'Plätze', 'Handregalfächer'].map(t => <th className="p-2" key={t}>{t}</th>)}</tr></thead>
        <tbody>{report.rows.map((r, i) => <tr className="border-t border-line" key={i}>
          <td className="p-2 font-mono">{r.input}</td><td className="p-2 font-mono"><GrundartikelLink code={r.merchant_sku} /></td>
          <td className="p-2">{({ ok: 'Bereit', kein_bestand: 'Kein Bestand – wird übernommen', unbekannt: 'Unbekannt', mehrdeutig: 'Mehrdeutig' })[r.status]}{r.duplicate && <span className="block text-xs text-ink-2">Duplikat: Familie nur einmal übernehmen</span>}</td>
          {[r.paletten.length, r.plaetze.length, r.handregal ? 1 : 0].map((n, j) => <td className="p-2 text-right tabular-nums" key={j}>{n}</td>)}</tr>)}</tbody>
      </table></div>
      <p className="text-xs text-ink-2">Unbekannte und mehrdeutige Zeilen werden ausgeschlossen. Artikel ohne Bestand bleiben zur Prüfung in der Analyse.</p>
      <Button disabled={!scope!.merchant_skus.length || create.isPending || preview.isPending} onClick={() => void start()}>{create.isPending ? 'Inventur wird angelegt…' : `Inventur mit ${scope!.merchant_skus.length} Artikeln anlegen`}</Button>
    </>}
  </section>
}
